import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileText, Loader2, Trash2, Calendar, User, Send, Pencil, X, Check, MessageSquare, Share2, Users } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { showToast } from '@/services/toast.service';
import { ellieArtifactService, type EllieArtifact, type ArtifactComment } from '@/services/ellieArtifact.service';
import { useTenantUsers } from '@/hooks/useTenantUsers';
import { UserAvatar } from '@/components/UserAvatar';
import { useAppAuth } from '@/hooks/useAppAuth';

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

// Markdown rendering (mermaid + component overrides) lives in one place so the
// artifact page and the in-conversation artifact viewer render identically.
import {
  markdownComponents,
  remarkPlugins,
} from '@/components/markdown/artifactMarkdown';



function CommentThread({ artifactId }: { artifactId: string }) {
  const queryClient = useQueryClient();
  const { user } = useAppAuth();
  const { data: tenantUsers } = useTenantUsers();
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['artifactComments', artifactId],
    queryFn: () => ellieArtifactService.listComments(artifactId),
    staleTime: 30 * 1000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['artifactComments', artifactId] });

  const addMutation = useMutation({
    mutationFn: (text: string) => ellieArtifactService.addComment(artifactId, text),
    onSuccess: () => {
      invalidate();
      setNewComment('');
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    },
    onError: (err: any) => showToast.error('Failed to add comment', { description: err.message }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ commentId, text }: { commentId: string; text: string }) =>
      ellieArtifactService.updateComment(artifactId, commentId, text),
    onSuccess: () => {
      invalidate();
      setEditingId(null);
    },
    onError: (err: any) => showToast.error('Failed to update comment', { description: err.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => ellieArtifactService.deleteComment(artifactId, commentId),
    onSuccess: invalidate,
    onError: (err: any) => showToast.error('Failed to delete comment', { description: err.message }),
  });

  const getUserById = (userId: string) => tenantUsers?.find(u => u.id === userId) ?? null;

  const handleSubmit = () => {
    const text = newComment.trim();
    if (!text) return;
    addMutation.mutate(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const startEdit = (comment: ArtifactComment) => {
    setEditingId(comment.id);
    setEditText(comment.text);
  };

  const submitEdit = () => {
    const text = editText.trim();
    if (!text || !editingId) return;
    updateMutation.mutate({ commentId: editingId, text });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Comments</h3>
        {comments.length > 0 && (
          <span className="text-xs text-muted-foreground">({comments.length})</span>
        )}
      </div>

      {/* Comment list */}
      <div className="flex-1 overflow-auto space-y-4 min-h-0">
        {isLoading && (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && comments.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">No comments yet.</p>
        )}
        {comments.map((comment) => {
          const isOwn = user?.id === comment.user_id;
          const isEditing = editingId === comment.id;

          return (
            <div key={comment.id} className="group">
              <div className="flex items-start justify-between gap-1">
                <UserAvatar
                  user={getUserById(comment.user_id)}
                  createdBy={comment.user_id}
                  size="sm"
                />
                {isOwn && !isEditing && (
                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => startEdit(comment)}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => deleteMutation.mutate(comment.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                )}
              </div>
              {isEditing ? (
                <div className="mt-1.5 space-y-1.5">
                  <Textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="text-sm min-h-[60px] resize-none"
                    autoFocus
                  />
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingId(null)}>
                      <X className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="h-7 px-2"
                      onClick={submitEdit}
                      disabled={updateMutation.isPending || !editText.trim()}
                    >
                      <Check className="h-3 w-3 mr-1" /> Save
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm mt-1 whitespace-pre-wrap break-words">{comment.text}</p>
              )}
              <p className="text-[10px] text-muted-foreground mt-1">
                {timeAgo(comment.created_at)}
                {comment.updated_at && ' (edited)'}
              </p>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* New comment input */}
      <div className="mt-3 pt-3 border-t border-border">
        <Textarea
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={handleKeyDown}
          className="text-sm min-h-[60px] resize-none"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-muted-foreground">⌘+Enter to send</span>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!newComment.trim() || addMutation.isPending}
          >
            {addMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <Send className="h-3 w-3 mr-1" />
            )}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

function ShareDialog({
  artifact,
  open,
  onOpenChange,
}: {
  artifact: EllieArtifact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { data: tenantUsers } = useTenantUsers();
  const [sharedWith, setSharedWith] = useState<string[]>(artifact.shared_with);
  const [sharedWithAll, setSharedWithAll] = useState(artifact.shared_with_all);

  useEffect(() => {
    if (open) {
      setSharedWith(artifact.shared_with);
      setSharedWithAll(artifact.shared_with_all);
    }
  }, [open, artifact.shared_with, artifact.shared_with_all]);

  const saveMutation = useMutation({
    mutationFn: () => ellieArtifactService.updateSharing(artifact.id, sharedWith, sharedWithAll),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ellieArtifact', artifact.id] });
      showToast.success('Sharing settings updated');
      onOpenChange(false);
    },
    onError: (err: any) => showToast.error('Failed to update sharing', { description: err.message }),
  });

  const toggleUser = (userId: string) => {
    setSharedWith((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const availableUsers = (tenantUsers || []).filter((u) => u.id !== artifact.user_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share Artifact</DialogTitle>
          <DialogDescription>Choose who can view this artifact.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="share-all" className="text-sm font-medium">Share with all users</Label>
            <Switch
              id="share-all"
              checked={sharedWithAll}
              onCheckedChange={setSharedWithAll}
            />
          </div>

          {!sharedWithAll && (
            <div className="space-y-2 max-h-64 overflow-auto">
              {availableUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No other users in this account.</p>
              ) : (
                availableUsers.map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center gap-3 rounded-md p-2 hover:bg-accent cursor-pointer"
                  >
                    <Checkbox
                      checked={sharedWith.includes(u.id)}
                      onCheckedChange={() => toggleUser(u.id)}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{u.name || u.email}</p>
                      {u.name && <p className="text-xs text-muted-foreground truncate">{u.email}</p>}
                    </div>
                  </label>
                ))
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function ArtifactDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAppAuth();
  const { data: tenantUsers } = useTenantUsers();
  const [shareOpen, setShareOpen] = useState(false);

  const { data: artifact, isLoading } = useQuery({
    queryKey: ['ellieArtifact', id],
    queryFn: () => ellieArtifactService.findById(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!artifact) {
    return (
      <div className="p-8 h-full overflow-auto">
        <div className="max-w-4xl mx-auto text-center py-12">
          <p className="text-muted-foreground">Artifact not found.</p>
          <Link to="/artifacts" className="text-sm text-primary hover:underline mt-4 inline-block">
            Back to Artifacts
          </Link>
        </div>
      </div>
    );
  }

  const createdDate = new Date(artifact.created_at);
  const isCreator = user?.id === artifact.user_id;

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 page-header-surface flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/artifacts">Artifacts</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{artifact.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-foreground">{artifact.name}</h1>
          </div>

          {artifact.description && (
            <p className="text-sm text-muted-foreground">{artifact.description}</p>
          )}

          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              <span>
                {createdDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                {' at '}
                {createdDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              <span>{(() => {
                const creatorUser = tenantUsers?.find(u => u.id === artifact.user_id);
                const creatorName = creatorUser?.name || creatorUser?.email;
                const agentLabel = artifact.agent_name
                  ? <><span className="font-semibold">{artifact.agent_name}</span> agent</>
                  : artifact.agent_id ? 'agent' : <span className="font-semibold">Ellie</span>;
                return <>Generated by {agentLabel}{creatorName && <> on behalf of <span className="font-semibold">{creatorName}</span></>}</>;
              })()}</span>
            </div>
          </div>
        </div>
        {isCreator && (
          <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}>
            <Share2 className="h-4 w-4 mr-1" />
            Share
          </Button>
        )}
      </div>

      {/* Content + Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Markdown content */}
        <div className="flex-1 overflow-auto">
          <div className="px-16 py-12">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
                {artifact.markdown}
              </ReactMarkdown>
            </div>
          </div>
        </div>

        {/* Sidebar - Comments */}
        <div className="w-80 flex-shrink-0 border-l border-border bg-muted/30 overflow-hidden p-5 flex flex-col">
          <CommentThread artifactId={artifact.id} />
        </div>
      </div>

      {isCreator && <ShareDialog artifact={artifact} open={shareOpen} onOpenChange={setShareOpen} />}
    </div>
  );
}

export default function ArtifactsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAppAuth();
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [artifactToDelete, setArtifactToDelete] = useState<EllieArtifact | null>(null);

  const canDelete = user?.roles?.some(r => r.key === 'Admin' || r.key === 'Builder') ?? false;

  const { data, isLoading } = useQuery({
    queryKey: ['ellieArtifacts'],
    queryFn: async () => {
      return await ellieArtifactService.find({ pageSize: 100 });
    },
    staleTime: 30 * 1000,
  });

  const artifacts = data?.records || [];

  const openDeleteDialog = useCallback((artifact: EllieArtifact, e: React.MouseEvent) => {
    e.stopPropagation();
    setArtifactToDelete(artifact);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!artifactToDelete) return;
    setDeleting(artifactToDelete.id);
    try {
      await ellieArtifactService.delete(artifactToDelete.id);
      showToast.success('Artifact deleted');
      queryClient.invalidateQueries({ queryKey: ['ellieArtifacts'] });
      setDeleteDialogOpen(false);
      setArtifactToDelete(null);
    } catch (err: any) {
      showToast.error('Failed to delete artifact', { description: err.message });
    } finally {
      setDeleting(null);
    }
  }, [artifactToDelete, queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 page-header-surface flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-foreground">Artifacts</h1>
          </div>

          <p className="text-sm text-muted-foreground">
            Ask Ellie to generate an artifact to save a snapshot of a conversation, or generate artifacts automatically using Agents.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-8">
      <div className="max-w-4xl mx-auto">

        {artifacts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">
                No artifacts yet. Ask Ellie to generate a report or write up her findings.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {artifacts.map((artifact) => (
              <Card
                key={artifact.id}
                className="cursor-pointer transition-colors hover:bg-accent"
                onClick={() => navigate(`/artifacts/${artifact.id}`)}
              >
                <CardContent className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{artifact.name}</p>
                      {artifact.description && (
                        <p className="text-sm text-muted-foreground truncate">{artifact.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {new Date(artifact.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => openDeleteDialog(artifact, e)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Artifact</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{artifactToDelete?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={!!deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={!!deleting}>
              {deleting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</>) : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
